import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cambiarEstadoImportacion,
  type EstadoEtapa,
  type Importacion,
  type ImportacionStatus,
} from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';

/** yyyy-mm-dd → dd/mm/yyyy. */
export const fechaImp = (iso: string | null | undefined) =>
  iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—';

/** Montos separados por moneda: no hay un tipo de cambio común para sumarlos. */
export function montosPorMoneda(montos: Record<string, number>): string {
  const partes = Object.entries(montos).map(([moneda, monto]) => formatMoney(monto, moneda as 'ARS' | 'USD' | 'MXN'));
  return partes.length > 0 ? partes.join(' + ') : '—';
}

export function EstadoImportacion({ status }: { status: ImportacionStatus }) {
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
        status === 'Abierta' && 'bg-sky-100 text-sky-700',
        status === 'Cerrada' && 'bg-emerald-100 text-emerald-700',
        status === 'Anulada' && 'bg-slate-200 text-slate-600',
      )}
    >
      {status}
    </span>
  );
}

const ESTADO_ETAPA: Record<EstadoEtapa, { texto: string; clases: string }> = {
  completada: { texto: 'Completada', clases: 'bg-emerald-100 text-emerald-700' },
  vencida: { texto: 'Vencida', clases: 'bg-rose-100 text-rose-700' },
  en_curso: { texto: 'En curso', clases: 'bg-sky-100 text-sky-700' },
  pendiente: { texto: 'Pendiente', clases: 'bg-slate-200 text-slate-600' },
};

export function EstadoEtapaBadge({ estado }: { estado: EstadoEtapa }) {
  const { texto, clases } = ESTADO_ETAPA[estado];
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
        clases,
      )}
    >
      {texto}
    </span>
  );
}

export function IconAction({
  children,
  onClick,
  title,
  destructive,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40',
        destructive
          ? 'border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20'
          : 'border-border text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

export function AnularImportacionDialog({
  importacion,
  onClose,
}: {
  importacion: Importacion | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const cerrar = () => {
    setError(null);
    onClose();
  };
  const mutation = useMutation({
    mutationFn: () => cambiarEstadoImportacion(importacion!.id, 'anular'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['importaciones'] });
      cerrar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo anular.'),
  });
  return (
    <Dialog open={importacion != null} onOpenChange={(o) => !o && !mutation.isPending && cerrar()}>
      <DialogContent className="max-w-md" processing={mutation.isPending && 'Anulando importación…'}>
        <DialogHeader>
          <DialogTitle>Anular importación N° {importacion?.numero}</DialogTitle>
          <DialogDescription>Sólo se puede anular si no tiene compras asociadas.</DialogDescription>
        </DialogHeader>
        <DialogBody>{error && <p className="text-sm font-medium text-destructive">{error}</p>}</DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Anular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
